import React from "react";
import { Plus, Trash2, MapPin, Globe2, CheckCircle2, AlertCircle } from "lucide-react";
import { US_STATES, COUNTRIES, PROVINCES, stateFromZip, tenYearCutoff, currentMonth } from "@/lib/geo";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";

export type Address = { street: string; city: string; state: string; zip: string; county: string; country: string; other_country?: string; province?: string; from: string; to: string };

export const emptyAddress = (): Address => ({ street: "", city: "", state: "", zip: "", county: "", country: "United States", from: "", to: "" });

const monthsBetween = (a: string, b: string) => {
  const [y1, m1] = a.split("-").map(Number);
  const [y2, m2] = b.split("-").map(Number);
  return (y2 - y1) * 12 + (m2 - m1);
};

export function coverage(addrs: Address[]) {
  const cutoff = tenYearCutoff();
  const earliest = addrs.map((a) => a.from).filter(Boolean).sort()[0];
  const months = earliest ? Math.min(120, Math.max(0, monthsBetween(earliest < cutoff ? cutoff : earliest, currentMonth()))) : 0;
  const states = Array.from(new Set(addrs.filter((a) => a.country === "United States" && a.state && (a.to || "9999") >= cutoff).map((a) => a.state)));
  const foreign = addrs.some((a) => a.country !== "United States");
  return { complete: !!earliest && earliest <= cutoff, years: (months / 12).toFixed(1), pct: Math.round((months / 120) * 100), states, foreign };
}

export function validateAddresses(addrs: Address[]): string | null {
  if (!addrs.length) return "Please add at least one address.";
  for (const [i, a] of addrs.entries()) {
    const n = `Address ${i + 1}`;
    if (!a.street || !a.city) return `${n}: street and city are required.`;
    if (a.country === "United States" && (!a.state || !a.zip)) return `${n}: state and ZIP code are required.`;
    if (a.country === "Other" && !a.other_country) return `${n}: please type the country name.`;
    if (!a.from) return `${n}: please enter the month you moved in.`;
    if (i > 0 && !a.to) return `${n}: please enter the month you moved out.`;
    if (a.to && a.to < a.from) return `${n}: move-out date is before move-in date.`;
  }
  if (!coverage(addrs).complete) return "Your addresses need to cover the last 10 years. Please add your earlier addresses.";
  return null;
}

function AddressCard({ a, i, onChange, onRemove }: { a: Address; i: number; onChange: (a: Address) => void; onRemove?: () => void }) {
  const set = (k: keyof Address) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const v = e.target.value;
    const next = { ...a, [k]: v };
    if (k === "zip" && a.country === "United States") {
      const st = stateFromZip(v);
      if (st) next.state = st;
    }
    onChange(next);
  };
  const us = a.country === "United States";
  return (
    <div className="card-soft p-5 sm:p-6" data-testid={`address-card-${i}`}>
      <div className="mb-5 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-lg font-bold">
          {us ? <MapPin className="h-5 w-5 text-[#0D9488]" /> : <Globe2 className="h-5 w-5 text-[#D97706]" />}
          {i === 0 ? "Current address" : `Previous address ${i}`}
        </h3>
        {onRemove && (
          <button type="button" onClick={onRemove} data-testid={`address-remove-${i}`} className="flex items-center gap-1 rounded-lg px-2 py-1 text-sm font-semibold text-[#B91C1C] hover:bg-[#FEE2E2]">
            <Trash2 className="h-4 w-4" /> Remove
          </button>
        )}
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <label className="block">
          <span className="field-label">Country</span>
          <select data-testid={`address-country-${i}`} className="field-input" value={a.country} onChange={set("country")}>
            {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        {a.country === "Other" && (
          <label className="block"><span className="field-label">Country name</span><input data-testid={`address-other-country-${i}`} className="field-input" value={a.other_country || ""} onChange={set("other_country")} /></label>
        )}
        <label className="block sm:col-span-2"><span className="field-label">Street address</span>
          {us ? (
            <AddressAutocomplete testId={`address-street-${i}`} value={a.street} placeholder="Start typing, e.g. 123 Main St" onChange={(v) => onChange({ ...a, street: v })}
              onPick={(s) => onChange({ ...a, street: s.street, city: s.city || a.city, state: s.state || a.state, zip: s.zip || a.zip, county: s.county || a.county })} />
          ) : <input data-testid={`address-street-${i}`} className="field-input" value={a.street} onChange={set("street")} placeholder="123 Main St, Apt 4" />}
        </label>
        <label className="block"><span className="field-label">City</span><input data-testid={`address-city-${i}`} className="field-input" value={a.city} onChange={set("city")} /></label>
        <label className="block">
          <span className="field-label">{us ? "ZIP code" : "Postal code"}</span>
          <input data-testid={`address-zip-${i}`} className="field-input" value={a.zip} onChange={set("zip")} />
          {us && <span className="field-hint block">We'll fill in the state for you.</span>}
        </label>
        {!us && (
          <label className="block">
            <span className="field-label">Province / State / Region</span>
            {PROVINCES[a.country] ? (
              <select data-testid={`address-province-${i}`} className="field-input" value={a.province || ""} onChange={set("province")}>
                <option value="">Select…</option>
                {PROVINCES[a.country].map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            ) : <input data-testid={`address-province-${i}`} className="field-input" value={a.province || ""} onChange={set("province")} placeholder="e.g. Ontario, Bavaria, Cebu" />}
          </label>
        )}
        {us && (
          <>
            <label className="block">
              <span className="field-label">State</span>
              <select data-testid={`address-state-${i}`} className="field-input" value={a.state} onChange={set("state")}>
                <option value="">Select…</option>
                {US_STATES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
              </select>
            </label>
            <label className="block"><span className="field-label">County</span><input data-testid={`address-county-${i}`} className="field-input" value={a.county} onChange={set("county")} placeholder="e.g. New Castle" /></label>
          </>
        )}
        <label className="block"><span className="field-label">Moved in (month & year)</span><input data-testid={`address-from-${i}`} type="month" className="field-input" value={a.from} onChange={set("from")} /></label>
        <label className="block">
          <span className="field-label">Moved out</span>
          {i === 0 ? <div className="field-input flex items-center text-[#64748B]">I live here now</div> : <input data-testid={`address-to-${i}`} type="month" className="field-input" value={a.to} onChange={set("to")} />}
        </label>
      </div>
      {!us && <p className="mt-4 rounded-xl bg-[#FEF3C7] px-4 py-3 text-sm text-[#92400E]">Addresses outside the United States are reviewed by our staff by hand. This may add a little time.</p>}
    </div>
  );
}

export default function StepAddresses({ addrs, onChange }: { addrs: Address[]; onChange: (a: Address[]) => void }) {
  const cov = coverage(addrs);
  const update = (i: number) => (a: Address) => onChange(addrs.map((x, j) => (j === i ? a : x)));
  return (
    <div className="space-y-6">
      <div className={`rounded-2xl border p-5 ${cov.complete ? "border-[#15803D]/30 bg-[#DCFCE7]/50" : "border-[#D97706]/30 bg-[#FEF3C7]/50"}`} data-testid="address-coverage-panel">
        <div className="flex items-center gap-3">
          {cov.complete ? <CheckCircle2 className="h-6 w-6 text-[#15803D]" /> : <AlertCircle className="h-6 w-6 text-[#B45309]" />}
          <p className="text-[15px] font-semibold text-[#1A2536]" data-testid="address-coverage-text">
            {cov.complete ? "Great — your last 10 years are covered." : `${cov.years} of 10 years covered. Please list every place you've lived in the last 10 years.`}
          </p>
        </div>
        <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-white">
          <div className={`h-full rounded-full transition-[width] duration-500 ${cov.complete ? "bg-[#15803D]" : "bg-[#D97706]"}`} style={{ width: `${cov.pct}%` }} />
        </div>
        {cov.states.length > 0 && (
          <p className="mt-3 text-sm text-[#475569]" data-testid="address-detected-states">
            States detected: {cov.states.map((s) => <span key={s} className="mr-1.5 rounded-md bg-white px-2 py-0.5 font-mono text-xs font-semibold text-[#1B365D]">{s}</span>)}
          </p>
        )}
      </div>
      {addrs.map((a, i) => <AddressCard key={i} a={a} i={i} onChange={update(i)} onRemove={i > 0 ? () => onChange(addrs.filter((_, j) => j !== i)) : undefined} />)}
      <button type="button" data-testid="address-add-button" className="btn-secondary w-full border-dashed" onClick={() => onChange([...addrs, emptyAddress()])}>
        <Plus className="h-5 w-5" /> Add a previous address
      </button>
    </div>
  );
}
